import React, { useState } from "react";

import { MoreIcon, Typography } from "@packages/ds";
import { FormattedMessage } from "react-intl";

import { Team } from "Api/teams/teams.types";
import { transformTeamPermissions } from "Api/teams/teams.utils";
import { TbodySkeleton } from "Components/TbodySkeleton";
import * as Dropdown from "ds/Dropdown";
import * as Table from "ds/Table";
import { capitalize } from "Libs/utils";
import { useGetProjectTeamAccesses } from "Reducers/access/teams/teams.slice";
import { projectSelector } from "Reducers/project/project";
import { ErrorWithMessage } from "src/organization/pages/settings/pages/components/ErrorWithMessage";
import useOrganizationFromURL from "src/organization/pages/settings/pages/members/hooks/useOrganizationFromURL";
import { Permission } from "src/organization/pages/settings/pages/teams/TeamList.styles";
import { TeamProjectsEmpty } from "src/organization/pages/settings/pages/teams/TeamProjectsEmpty";
import { useAppSelector } from "Store/hooks";

import { RemoveTeamFromProjectModal } from "./RemoveTeamFromProjectModal";

type ProjectAccessTeamsListProps = {
  projectId: string;
  onAdd: () => void;
};

export const ProjectAccessTeamsList = (props: ProjectAccessTeamsListProps) => {
  const [teamToBeRemoved, setTeamToBeRemoved] = useState<Team>();
  const organization = useOrganizationFromURL();
  const project = useAppSelector(
    state =>
      organization &&
      projectSelector(state, {
        organizationId: organization.name,
        projectId: props.projectId
      })
  );
  const [hasScrolled, onScroll] = Table.useHasScrolled();
  const [teams, isLoading, error, hasMore, isLoadingMore, { loadMore }] =
    useGetProjectTeamAccesses({
      projectId: props.projectId
    });

  if (error) return <ErrorWithMessage error={error} />;

  if (teams && teams.length === 0)
    return <TeamProjectsEmpty onAdd={props.onAdd} />;

  return (
    <Table.TableFlex>
      <Table.Thead hasBorderBottom={hasScrolled}>
        <Table.Th width="30%">
          <FormattedMessage id="teams.list.table.header.name" />
        </Table.Th>
        <Table.Th width="10%">
          <FormattedMessage id="teams.list.table.header.users" />
        </Table.Th>
        <Table.Th width="60%">
          <FormattedMessage id="teams.list.table.header.permissions" />
        </Table.Th>
        <Table.Th width="32px" />
      </Table.Thead>
      {isLoading || isLoading === undefined ? (
        <TbodySkeleton />
      ) : (
        <Table.Tbody
          onScroll={onScroll}
          hasMore={hasMore}
          loadMore={loadMore}
          height="calc(100vh - 425px)"
        >
          {teams?.map(team => (
            <Table.Tr key={team.id}>
              <Table.Td width="30%">
                <Typography.TextOverflow
                  as={Typography.BodyText}
                  weight="semiBold"
                >
                  {team.label}
                </Typography.TextOverflow>
              </Table.Td>
              <Table.Td width="10%">
                <Typography.BodyText weight="semiBold">
                  {team.counts.member_count} <FormattedMessage id="users" />
                </Typography.BodyText>
              </Table.Td>
              <Table.Td width="60%">
                {transformTeamPermissions(team.project_permissions).map(
                  ([environmentType, access]) => (
                    <Permission key={environmentType}>
                      <Typography.BodyText>
                        {capitalize(access)}
                      </Typography.BodyText>
                      <Typography.Label as="span">
                        {capitalize(environmentType)}
                      </Typography.Label>
                    </Permission>
                  )
                )}
              </Table.Td>
              <Table.Td width="32px">
                <Dropdown.Root id="project_access_teams_list">
                  <Dropdown.Trigger aria-label="access-table-more-trigger">
                    <MoreIcon aria-label="access-table-more-icon" />
                  </Dropdown.Trigger>
                  <Dropdown.Portal>
                    <Dropdown.Content align="end" style={{ minWidth: "164px" }}>
                      <Dropdown.LinkItem
                        to={`/${organization?.name}/-/settings/teams/${team.id}`}
                      >
                        <FormattedMessage id="view_team" />
                      </Dropdown.LinkItem>
                      <Dropdown.Item onClick={() => setTeamToBeRemoved(team)}>
                        <FormattedMessage id="remove_team" />
                      </Dropdown.Item>
                    </Dropdown.Content>
                  </Dropdown.Portal>
                </Dropdown.Root>
              </Table.Td>
            </Table.Tr>
          ))}
          {isLoadingMore && <TbodySkeleton />}
        </Table.Tbody>
      )}
      {teamToBeRemoved && (
        <RemoveTeamFromProjectModal
          team={teamToBeRemoved}
          projectId={props.projectId}
          projectName={project?.name}
          onClose={() => setTeamToBeRemoved(undefined)}
        />
      )}
    </Table.TableFlex>
  );
};
